import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { ChevronRight, Home, MapPin, Send, CheckCircle2, Lock, History, FileText, Trash2, Badge, User, Upload, Download, AlertTriangle, Image as ImageIcon, Edit2, Plus, Archive, Filter, TrendingUp, Clock, PoundSterling, AlertCircle, Star, Calendar, X } from 'lucide-react';

interface Note {
  id: string;
  author: string;
  role: string;
  avatar: string;
  timestamp: string;
  content: string;
  priority?: 'URGENT' | 'NORMAL';
  attachment?: { name: string; icon: string };
  isPrivate: boolean;
}

interface DocumentItem {
  id: string;
  name: string;
  uploadDate: string;
  fileSize: string;
  status: 'VERIFIED' | 'EXPIRING' | 'PENDING';
  category: string;
}

interface SitePhoto {
  id: string;
  url: string;
  title: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  issueDate: string;
  status: 'OVERDUE' | 'PENDING' | 'PAID';
  totalValue: string;
  amountPaid: string;
  balance: string;
}

interface Quote {
  id: string;
  quoteNumber: string;
  service: string;
  serviceProvider: string;
  issuedDate: string;
  value: string;
  status: 'PENDING APPROVAL' | 'UNDER REVIEW' | 'READY TO SEND';
}

interface Order {
  id: string;
  jobId: string;
  serviceType: string;
  icon: string;
  scheduledDate: string;
  provider: string;
  providerAvatar: string;
  value: string;
  status: 'IN PROGRESS' | 'PENDING' | 'OVERDUE' | 'COMPLETED';
}

const AdminPropertyDetailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('documents');
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState<Note[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [propertyLoading, setPropertyLoading] = useState(true);

  const propertyId = searchParams.get('id') || 'SOV-88219';
  const propertyName = searchParams.get('address') || 'The Obsidian Heights';

  // Fetch property data from database
  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const [notesRes, invoicesRes, quotesRes, ordersRes, docsRes] = await Promise.all([
          apiService.request(`/admin/properties/${propertyId}/notes`, 'GET'),
          apiService.request(`/admin/properties/${propertyId}/invoices`, 'GET'),
          apiService.request(`/admin/properties/${propertyId}/quotes`, 'GET'),
          apiService.request(`/admin/properties/${propertyId}/orders`, 'GET'),
          apiService.request(`/admin/properties/${propertyId}/documents`, 'GET')
        ]);

        if (notesRes.success && notesRes.data) setNotes(notesRes.data);
        if (invoicesRes.success && invoicesRes.data) setInvoices(invoicesRes.data);
        if (quotesRes.success && quotesRes.data) setQuotes(quotesRes.data);
        if (ordersRes.success && ordersRes.data) setOrders(ordersRes.data);
        if (docsRes.success && docsRes.data) setDocuments(docsRes.data);
      } catch (error) {
        console.error('Failed to fetch property data:', error);
      } finally {
        setPropertyLoading(false);
      }
    };

    if (propertyId) {
      fetchPropertyData();
    }
  }, [propertyId]);

  // Use fetched notes
  const displayNotes = notes;

  const [propertyDetails, setPropertyDetails] = useState<any>(null);
  const [propertyStats, setPropertyStats] = useState<any>(null);

  useEffect(() => {
    const fetchPropertyStats = async () => {
      try {
        const response = await apiService.request(`/admin/properties/${propertyId}/stats`, 'GET');
        if (response.success && response.data) {
          setPropertyStats(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch property stats:', error);
      }
    };
    if (propertyId) {
      fetchPropertyStats();
    }
  }, [propertyId]);

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      try {
        const response = await apiService.request(`/admin/properties/${propertyId}`, 'GET');
        if (response.success && response.data) {
          setPropertyDetails(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch property details:', error);
      }
    };
    if (propertyId) {
      fetchPropertyDetails();
    }
  }, [propertyId]);

  const property = {
    id: propertyId,
    name: propertyDetails?.name || propertyName,
    address: propertyDetails?.address || '—',
    status: propertyDetails?.status || 'ACTIVE PORTFOLIO',
    appraisalValue: propertyDetails?.appraisal_value ? `£${Number(propertyDetails.appraisal_value).toLocaleString()}` : '—',
  };
  // (audit 2026-06-11) — JSX further down references `propertyData` but only
  // `property` was declared. Alias to keep both names working.
  const propertyData = property;

  const tabs = [
    { id: 'orders', label: 'ORDERS' },
    { id: 'quotes', label: 'QUOTES' },
    { id: 'invoices', label: 'INVOICES' },
    { id: 'documents', label: 'DOCUMENTS' },
    { id: 'notes', label: 'NOTES' },
  ];

  // Use fetched documents or empty arrays
  const complianceDocuments: DocumentItem[] = documents.filter(d => d.category === 'COMPLIANCE DOCUMENTS');
  const reports: DocumentItem[] = documents.filter(d => d.category === 'REPORTS');
  const sitePhotos: SitePhoto[] = [];
  const activities = [];

  // Use fetched data
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  const totalInvoices = invoices.length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return 'text-teal-600 bg-teal-50';
      case 'EXPIRING':
        return 'text-red-600 bg-red-50';
      case 'PENDING':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusBorderColor = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return 'border-l-4 border-l-teal-600';
      case 'EXPIRING':
        return 'border-l-4 border-l-red-600';
      default:
        return '';
    }
  };

  // Use fetched data or defaults
  const displayOrders = orders;
  const displayQuotes = quotes;
  const displayInvoices = invoices;

  const formatPence = (pence: number) => `£${(pence / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Handler: Edit property
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', address_line1: '', address_line2: '', city: '', postcode: '', country: '',
    property_type: '', bedrooms: '', bathrooms: '', size_sqft: '', notes: '',
  });
  const [savingProperty, setSavingProperty] = useState(false);

  const openEditModal = () => {
    setEditForm({
      name: propertyDetails?.name || '',
      address_line1: propertyDetails?.address_line1 || '',
      address_line2: propertyDetails?.address_line2 || '',
      city: propertyDetails?.city || '',
      postcode: propertyDetails?.postcode || '',
      country: propertyDetails?.country || '',
      property_type: propertyDetails?.property_type || '',
      bedrooms: propertyDetails?.bedrooms != null ? String(propertyDetails.bedrooms) : '',
      bathrooms: propertyDetails?.bathrooms != null ? String(propertyDetails.bathrooms) : '',
      size_sqft: propertyDetails?.size_sqft != null ? String(propertyDetails.size_sqft) : '',
      notes: propertyDetails?.notes || '',
    });
    setShowEditModal(true);
  };

  const handleSaveProperty = async () => {
    if (!editForm.name.trim() || !editForm.address_line1.trim()) {
      alert('Property name and address are required');
      return;
    }
    setSavingProperty(true);
    try {
      const response = await apiService.request(`/admin/properties/${propertyId}/edit`, 'PUT', {
        ...editForm,
        bedrooms: editForm.bedrooms === '' ? null : Number(editForm.bedrooms),
        bathrooms: editForm.bathrooms === '' ? null : Number(editForm.bathrooms),
        size_sqft: editForm.size_sqft === '' ? null : Number(editForm.size_sqft),
      });
      if (response.success) {
        setPropertyDetails((prev: any) => ({ ...prev, ...editForm }));
        setShowEditModal(false);
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Edit property error:', error);
      alert('Failed to update property: ' + error.message);
    } finally {
      setSavingProperty(false);
    }
  };

  // Handler: Add note to property
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      const response = await apiService.request(`/admin/properties/${propertyId}/notes`, 'POST', {
        content: noteText,
        author: localStorage.getItem('adminName') || 'Admin User',
        role: 'ADMIN',
        isPrivate: true,
        createdBy: localStorage.getItem('adminId'),
        createdAt: new Date().toISOString()
      });
      if (response.success) {
        setNoteText('');
        alert('✓ Note added successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Add note error:', error);
      alert('Failed to add note: ' + error.message);
    }
  };

  // Handler: Upload document
  const handleUploadDocument = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('propertyId', propertyId);
      formData.append('uploadedBy', localStorage.getItem('adminId') || '');
      
      const response = await apiService.request(`/admin/properties/${propertyId}/documents/upload`, 'POST', formData);
      if (response.success) {
        alert('✓ Document uploaded successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Upload document error:', error);
      alert('Failed to upload document: ' + error.message);
    }
  };

  // Handler: Delete document
  const handleDeleteDocument = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      const response = await apiService.request(`/admin/properties/${propertyId}/documents/${documentId}`, 'DELETE');
      if (response.success) {
        alert('✓ Document deleted successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Delete document error:', error);
      alert('Failed to delete document: ' + error.message);
    }
  };

  // Handler: Archive property
  const handleArchiveProperty = async () => {
    if (!confirm('Archive this property? This action can be reversed.')) return;
    try {
      const response = await apiService.request(`/admin/properties/${propertyId}/archive`, 'PATCH', {
        status: 'ARCHIVED',
        archivedBy: localStorage.getItem('adminId'),
        archivedAt: new Date().toISOString()
      });
      if (response.success) {
        alert('✓ Property archived successfully');
        navigate('/admin/properties');
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Archive property error:', error);
      alert('Failed to archive property: ' + error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>{propertyName} - Property Details</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-white min-h-screen">
          {/* Header */}
          <div className="border-b border-gray-200">
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg flex items-center justify-center text-white font-bold text-3xl shadow-lg">
                    🏢
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="w-4 h-4 text-teal-600" />
                      <span className="text-xs font-bold text-teal-600 uppercase tracking-widest">
                        {propertyData.status}
                      </span>
                      <span className="text-xs font-bold text-gray-500">• ID: {propertyData.id}</span>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{propertyData.name}</h1>
                    <div className="flex items-center gap-1 text-gray-600 text-sm">
                      <MapPin className="w-4 h-4" />
                      {propertyData.address}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Appraisal Value</p>
                  <p className="text-3xl font-bold text-gray-900">{propertyData.appraisalValue}</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-8 flex items-center gap-12 border-t border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 font-bold text-xs uppercase tracking-widest border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'text-gray-900 border-teal-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div className="max-w-7xl">
                <div className="grid grid-cols-3 gap-8">
                  {/* Main Content */}
                  <div className="col-span-2 space-y-8">
                    {/* Compliance Documents Section */}
                    <div>
                      <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">
                        Compliance Documents • {complianceDocuments.length}
                      </h2>
                      <div className="space-y-3">
                        {complianceDocuments.map((doc) => (
                          <div
                            key={doc.id}
                            className={`p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors ${getStatusBorderColor(doc.status)}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                {doc.status === 'EXPIRING' ? (
                                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <FileText className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1">
                                  <p className="font-bold text-gray-900">{doc.name}</p>
                                  <p className="text-xs text-gray-600 mt-1">UPLOADED {doc.uploadDate} • {doc.fileSize}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${getStatusColor(doc.status)}`}>
                                  • {doc.status}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Site Photos Section */}
                    <div>
                      <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">
                        Site Photos • {sitePhotos.length}
                      </h2>
                      <div className="grid grid-cols-2 gap-4">
                        {sitePhotos.map((photo) => (
                          <div
                            key={photo.id}
                            className={`${photo.url} h-32 rounded-lg flex items-center justify-center text-white cursor-pointer hover:opacity-90 transition-opacity shadow-md`}
                          >
                            <ImageIcon className="w-8 h-8 opacity-50" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Reports Section */}
                    <div>
                      <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">
                        Reports • {reports.length}
                      </h2>
                      <div className="space-y-3">
                        {reports.map((report) => (
                          <div key={report.id} className="p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                <FileText className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <p className="font-bold text-gray-900">{report.name}</p>
                                  <p className="text-xs text-gray-600 mt-1">UPLOADED {report.uploadDate} • {report.fileSize}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${getStatusColor(report.status)}`}>
                                  • {report.status}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Sidebar */}
                  <div className="space-y-6">
                    {/* Command Actions */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <h3 className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">Command Actions</h3>
                      <div className="space-y-3 flex flex-col">
                        <label className="w-full px-4 py-2 bg-teal-600 text-white font-bold text-sm rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 cursor-pointer">
                          <Upload className="w-4 h-4" />
                          Upload Document
                          <input
                            type="file"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const fd = new FormData();
                                fd.append('file', file);
                                const res: any = await apiService.request(`/admin/properties/${propertyId}/documents`, 'POST', fd);
                                if (res?.success) { alert('Document uploaded'); window.location.reload(); }
                                else alert('Error: ' + (res?.message || 'Upload failed'));
                              } catch (err: any) { alert('Failed: ' + (err?.message || 'Network error')); }
                            }}
                          />
                        </label>
                        <button
                          onClick={openEditModal}
                          className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit Property
                        </button>
                        <button
                          onClick={() => navigate(`/admin/orders-jobs?propertyId=${propertyId}&action=new`)}
                          className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Manual Order
                        </button>
                        <button
                          onClick={async () => {
                            const note = window.prompt('Add a note to this property:');
                            if (!note || !note.trim()) return;
                            try {
                              const res: any = await apiService.request(`/admin/properties/${propertyId}/notes`, 'POST', { note });
                              if (res?.success) { alert('Note added'); window.location.reload(); }
                              else alert('Error: ' + (res?.message || 'Failed to add note'));
                            } catch (err: any) { alert('Failed: ' + (err?.message || 'Network error')); }
                          }}
                          className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                          <FileText className="w-4 h-4" />
                          Add Note
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Archive this property? It will be hidden from active lists.')) return;
                            try {
                              const res: any = await apiService.request(`/admin/properties/${propertyId}/archive`, 'POST', {});
                              if (res?.success) { alert('Property archived'); navigate('/admin/properties'); }
                              else alert('Error: ' + (res?.message || 'Failed to archive'));
                            } catch (err: any) { alert('Failed: ' + (err?.message || 'Network error')); }
                          }}
                          className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                          <Archive className="w-4 h-4" />
                          Archive
                        </button>
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                      <h3 className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">Recent Activity</h3>
                      <div className="space-y-4">
                        {activities.map((activity, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <div className="w-2 h-2 bg-teal-600 rounded-full mt-2 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-bold text-gray-900">{activity.message}</p>
                              <p className="text-xs text-gray-600 mt-1">{activity.time}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="max-w-6xl">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                  {/* Header with Metrics */}
                  <div className="grid grid-cols-4 gap-4 mb-8">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Urgent Alerts</p>
                      <p className="text-3xl font-bold text-red-600">{String(propertyStats?.urgentAlerts ?? 0).padStart(2, '0')}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Vendor Satisfaction</p>
                      <p className="text-3xl font-bold text-gray-900">{propertyStats?.vendorSatisfaction != null ? Number(propertyStats.vendorSatisfaction).toFixed(1) : '—'}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Pending Invoices</p>
                      <p className="text-3xl font-bold text-gray-900">{String(propertyStats?.pendingQuotes ?? 0).padStart(2, '0')}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Budget Utilization</p>
                      <div className="flex items-center gap-3">
                        <p className="text-3xl font-bold text-gray-900">{propertyStats?.budgetUtilization ?? 0}%</p>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-teal-600" style={{width: `${propertyStats?.budgetUtilization ?? 0}%`}}></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Active & Recent Orders</h2>
                    <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors">
                      <Filter className="w-4 h-4" />
                      FILTER
                    </button>
                  </div>

                  {/* Orders Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Job ID</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Service Type</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Scheduled Date</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Provider</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Value</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayOrders.map((order) => {
                          const statusConfig = {
                            'IN PROGRESS': { dot: 'bg-teal-600', text: 'text-teal-600' },
                            'PENDING': { dot: 'bg-gray-400', text: 'text-gray-600' },
                            'OVERDUE': { dot: 'bg-red-600', text: 'text-red-600' },
                            'COMPLETED': { dot: 'bg-teal-600', text: 'text-teal-600' },
                          };
                          const colors = statusConfig[order.status as keyof typeof statusConfig] || { dot: 'bg-gray-400', text: 'text-gray-600' };

                          return (
                            <tr key={order.id} className="border-b border-gray-200 hover:bg-white transition-colors">
                              <td className="px-4 py-4 font-bold text-gray-900">{order.jobId}</td>
                              <td className="px-4 py-4 text-sm text-gray-600">{order.serviceType}</td>
                              <td className="px-4 py-4 text-sm text-gray-600">{order.scheduledDate}</td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gray-300 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                    {order.providerAvatar}
                                  </div>
                                  <span className="text-sm text-gray-900">{order.provider}</span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm font-bold text-gray-900">{order.value}</td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                                  <span className={`text-xs font-bold uppercase tracking-widest ${colors.text}`}>
                                    {order.status}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm">
                                <button className="text-teal-600 hover:text-teal-700 font-bold">View</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                      Showing {orders.length} of {propertyStats?.totalOrders ?? orders.length} orders
                    </p>
                    <div className="flex items-center gap-2">
                      <button className="px-3 py-2 bg-white border border-gray-300 text-gray-600 font-bold text-sm rounded hover:bg-gray-50">1</button>
                      <button className="px-3 py-2 bg-white border border-gray-300 text-gray-600 font-bold text-sm rounded hover:bg-gray-50">2</button>
                      <button className="px-3 py-2 bg-white border border-gray-300 text-gray-600 font-bold text-sm rounded hover:bg-gray-50">3</button>
                      <span className="text-gray-400">...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quotes Tab */}
            {activeTab === 'quotes' && (
              <div className="max-w-6xl">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                  {/* Top Metrics */}
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Total Outstanding</p>
                      <p className="text-4xl font-bold text-gray-900">{formatPence(propertyStats?.totalOutstandingPence ?? 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-1">Active Quotes</p>
                      <p className="text-4xl font-bold text-gray-900">{propertyStats?.activeQuotes ?? 0}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <select className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold text-sm rounded-lg focus:outline-none focus:border-teal-600">
                        <option>SORT BY: NEWEST</option>
                        <option>OLDEST</option>
                        <option>HIGHEST VALUE</option>
                        <option>LOWEST VALUE</option>
                      </select>
                      <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors">
                        <Filter className="w-4 h-4" />
                        FILTER
                      </button>
                    </div>
                  </div>

                  {/* Quotes Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Quote #</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Service</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Issued Date</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Value</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayQuotes.map((quote) => {
                          const statusConfig = {
                            'PENDING APPROVAL': { dot: 'bg-red-600', text: 'text-red-600' },
                            'UNDER REVIEW': { dot: 'bg-gray-400', text: 'text-gray-600' },
                            'READY TO SEND': { dot: 'bg-teal-600', text: 'text-teal-600' },
                          };
                          const colors = statusConfig[quote.status as keyof typeof statusConfig] || { dot: 'bg-gray-400', text: 'text-gray-600' };

                          return (
                            <tr key={quote.id} className="border-b border-gray-200 hover:bg-white transition-colors">
                              <td className="px-4 py-4 font-bold text-gray-900">{quote.quoteNumber}</td>
                              <td className="px-4 py-4">
                                <div>
                                  <p className="text-sm font-bold text-gray-900">{quote.service}</p>
                                  <p className="text-xs text-gray-500">{quote.serviceProvider}</p>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">{quote.issuedDate}</td>
                              <td className="px-4 py-4 text-sm font-bold text-gray-900">{quote.value}</td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                                  <span className={`text-xs font-bold uppercase tracking-widest ${colors.text}`}>
                                    {quote.status}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm">
                                <button className="text-teal-600 hover:text-teal-700 font-bold">View</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                      Showing {quotes.length} of {propertyStats?.activeQuotes ?? quotes.length} active quotes
                    </p>
                    <div className="flex items-center gap-2">
                      <button className="px-3 py-2 bg-teal-600 text-white font-bold text-sm rounded hover:bg-teal-700">1</button>
                      <button className="px-3 py-2 bg-white border border-gray-300 text-gray-600 font-bold text-sm rounded hover:bg-gray-50">2</button>
                      <button className="px-3 py-2 bg-white border border-gray-300 text-gray-600 font-bold text-sm rounded hover:bg-gray-50">3</button>
                    </div>
                  </div>

                  {/* Bottom Metrics */}
                  <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-gray-200">
                    <div className="flex items-center gap-4">
                      <TrendingUp className="w-8 h-8 text-teal-600" />
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Quote Conversion</p>
                        <p className="text-2xl font-bold text-gray-900">{propertyStats?.quoteConversion ?? 0}%</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Clock className="w-8 h-8 text-teal-600" />
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Avg. Approval Time</p>
                        <p className="text-2xl font-bold text-gray-900">{propertyStats?.avgApprovalDays != null ? `${propertyStats.avgApprovalDays} Days` : '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <PoundSterling className="w-8 h-8 text-teal-600" />
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">YTD Expenditure</p>
                        <p className="text-2xl font-bold text-gray-900">{formatPence(propertyStats?.ytdExpenditurePence ?? 0)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div className="max-w-6xl">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Invoice Ledger</h2>
                    <div className="flex items-center gap-3">
                      <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50 transition-colors">
                        <Filter className="w-4 h-4" />
                        FILTER
                      </button>
                      <button className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white font-bold text-sm rounded-lg hover:bg-teal-700 transition-colors">
                        <Download className="w-4 h-4" />
                        EXPORT CSV
                      </button>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Invoice #</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Issue Date</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Total Value</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Amount Paid</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Balance</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayInvoices.map((invoice) => {
                          const statusColor = {
                            OVERDUE: { dot: 'bg-red-600', text: 'text-red-600', bg: 'bg-red-50' },
                            PENDING: { dot: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-50' },
                            PAID: { dot: 'bg-teal-600', text: 'text-teal-600', bg: 'bg-teal-50' },
                          };
                          const colors = statusColor[invoice.status];
                          const balanceColor = invoice.status === 'PAID' ? 'text-teal-600' : 'text-red-600';

                          return (
                            <tr key={invoice.id} className="border-b border-gray-200 hover:bg-white transition-colors">
                              <td className="px-4 py-4 font-bold text-gray-900">{invoice.invoiceNumber}</td>
                              <td className="px-4 py-4 text-sm text-gray-600">{invoice.issueDate}</td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                                  <span className={`text-xs font-bold uppercase tracking-widest ${colors.text}`}>
                                    {invoice.status}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm font-bold text-gray-900">{invoice.totalValue}</td>
                              <td className="px-4 py-4 text-sm font-bold text-gray-900">{invoice.amountPaid}</td>
                              <td className={`px-4 py-4 text-sm font-bold ${balanceColor}`}>{invoice.balance}</td>
                              <td className="px-4 py-4 text-sm">
                                <button className="text-teal-600 hover:text-teal-700 font-bold">View</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                    <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">
                      Showing {invoices.length} of {totalInvoices} Invoices
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 text-gray-600 font-bold text-sm hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        className="px-4 py-2 text-gray-600 font-bold text-sm hover:text-gray-900"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div className="max-w-4xl">
                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                  <div className="flex items-start gap-3 mb-4">
                    <FileText className="w-5 h-5 text-gray-400 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h2 className="font-bold text-gray-900">ADD AN INTERNAL NOTE</h2>
                    </div>
                    <span className="inline-block bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">
                      PRIVATE TO STAFF
                    </span>
                  </div>

                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Type administrative updates, flags, or private remarks here..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200 resize-none"
                    rows={4}
                  />

                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-3" />
                    <button
                      className={`px-6 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors ${
                        noteText.trim()
                          ? 'bg-teal-600 text-white hover:bg-teal-700'
                          : 'bg-gray-200 text-gray-600 cursor-not-allowed'
                      }`}
                    >
                      <Send className="w-4 h-4" />
                      Post Note
                    </button>
                  </div>
                </div>

                <div className="space-y-6">
                  {notes.map((note) => (
                    <div key={note.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-gray-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                          {note.avatar}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-bold text-gray-900">{note.author}</p>
                            <span className="text-xs font-bold text-gray-500 uppercase">• {note.role}</span>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">{note.timestamp}</p>
                          <p className="text-sm text-gray-700">{note.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Edit Property Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">Edit Property</h2>
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Property Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Address Line 1</label>
                  <input
                    type="text"
                    value={editForm.address_line1}
                    onChange={(e) => setEditForm({ ...editForm, address_line1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Address Line 2</label>
                  <input
                    type="text"
                    value={editForm.address_line2}
                    onChange={(e) => setEditForm({ ...editForm, address_line2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">City</label>
                    <input
                      type="text"
                      value={editForm.city}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Postcode</label>
                    <input
                      type="text"
                      value={editForm.postcode}
                      onChange={(e) => setEditForm({ ...editForm, postcode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Country</label>
                    <input
                      type="text"
                      value={editForm.country}
                      onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Property Type</label>
                    <input
                      type="text"
                      value={editForm.property_type}
                      onChange={(e) => setEditForm({ ...editForm, property_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Bedrooms</label>
                    <input
                      type="number"
                      value={editForm.bedrooms}
                      onChange={(e) => setEditForm({ ...editForm, bedrooms: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Bathrooms</label>
                    <input
                      type="number"
                      value={editForm.bathrooms}
                      onChange={(e) => setEditForm({ ...editForm, bathrooms: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Size (sqft)</label>
                    <input
                      type="number"
                      value={editForm.size_sqft}
                      onChange={(e) => setEditForm({ ...editForm, size_sqft: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2 block">Notes</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-bold text-sm rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProperty}
                  disabled={savingProperty}
                  className="px-4 py-2 bg-teal-600 text-white font-bold text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {savingProperty ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </>
  );
};

export default AdminPropertyDetailPage;
